import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, ArrowLeft } from 'lucide-react';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import { SkeletonBlock } from '@/components/transitions/Skeleton';
import CenterRouteLoader from '@/components/transitions/CenterRouteLoader';
import { api, SITE_URL } from '@/lib/api';
import { breadcrumbJsonLd, graph } from '@/lib/jsonld';

function renderMarkdown(md) {
  // Lightweight markdown: # ## ###, lists, bold, paragraphs
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let listType = null; let listItems = [];
  const flushList = () => {
    if (listType) {
      const Tag = listType;
      out.push(<Tag key={`l-${out.length}`}>{listItems.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(li) }} />)}</Tag>);
      listType = null; listItems = [];
    }
  };
  function inline(s) { return s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'); }
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('## ')) { flushList(); out.push(<h2 key={i}>{line.slice(3)}</h2>); }
    else if (line.startsWith('### ')) { flushList(); out.push(<h3 key={i}>{line.slice(4)}</h3>); }
    else if (line.startsWith('# ')) { flushList(); out.push(<h2 key={i}>{line.slice(2)}</h2>); }
    else if (/^\s*[-*]\s+/.test(line)) {
      if (listType !== 'ul') { flushList(); listType = 'ul'; }
      listItems.push(line.replace(/^\s*[-*]\s+/, ''));
    }
    else if (/^\s*\d+\.\s+/.test(line)) {
      if (listType !== 'ol') { flushList(); listType = 'ol'; }
      listItems.push(line.replace(/^\s*\d+\.\s+/, ''));
    }
    else if (line.trim() === '') { flushList(); }
    else { flushList(); out.push(<p key={i} dangerouslySetInnerHTML={{ __html: inline(line) }} />); }
  }
  flushList();
  return out;
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((err) => { if (err?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <>
        <SEOHead title="Loading" description="Loading article" path={`/blog/${slug}`} />
        <section className="bg-[#F5F7FB]" data-testid="blog-detail-skeleton">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-4">
            <SkeletonBlock className="h-3 w-32 skeleton-shimmer-light" />
            <SkeletonBlock className="h-8 w-3/4 skeleton-shimmer-light" />
            <SkeletonBlock className="h-3 w-full skeleton-shimmer-light" />
            <SkeletonBlock className="h-3 w-5/6 skeleton-shimmer-light" />
            <div className="pt-6 space-y-3">
              <SkeletonBlock className="h-3 w-full skeleton-shimmer-light" />
              <SkeletonBlock className="h-3 w-full skeleton-shimmer-light" />
              <SkeletonBlock className="h-3 w-4/5 skeleton-shimmer-light" />
            </div>
          </div>
        </section>
        <CenterRouteLoader label="Loading article…" />
      </>
    );
  }
  if (notFound || !post) {
    return (
      <>
        <SEOHead title="Article not found" description="The article you’re looking for is no longer available." path={`/blog/${slug}`} />
        <PageHeader breadcrumbs={[{ label: 'Blog', to: '/blog' }, { label: 'Not found' }]} title="Article not found" subtitle="The article you’re looking for is no longer available." />
        <section className="bg-[#F5F7FB]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#2563EB] font-semibold"><ArrowLeft className="h-4 w-4" /> Back to blog</Link>
          </div>
        </section>
      </>
    );
  }
  const date = new Date(post.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const articleNode = {
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: `${SITE_URL}/og-image.png`,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: { '@type': 'Organization', name: post.author || 'Ads Dashbound', url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: 'Ads Dashbound',
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/og-image.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
    inLanguage: 'en',
  };
  const jsonLd = graph(
    breadcrumbJsonLd([
      { label: 'Home', path: '/' },
      { label: 'Blog', path: '/blog' },
      { label: post.title },
    ]),
    articleNode,
  );
  return (
    <>
      <SEOHead title={post.title} description={post.excerpt} path={`/blog/${post.slug}`} type="article" jsonLd={jsonLd} />
      <PageHeader
        breadcrumbs={[{ label: 'Blog', to: '/blog' }, { label: post.title }]}
        eyebrow={post.category}
        title={post.title}
        subtitle={post.excerpt}
      >
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readTime}</span>
          <span>{date}</span>
        </div>
      </PageHeader>
      <section className="bg-[#F5F7FB]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <article className="rounded-2xl bg-white border border-slate-900/10 p-6 sm:p-10 shadow-[0_10px_30px_rgba(2,6,23,0.06)]">
            <div className="prose-dashbound">{renderMarkdown(post.body)}</div>
          </article>
          <div className="mt-8">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#2563EB] font-semibold hover:underline"><ArrowLeft className="h-4 w-4" /> Back to blog</Link>
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
